// Ponto de entrada da interface do Ordo (empacotado com esbuild).

import * as store from "./store.js";
import * as router from "./router.js";
import * as sidebar from "./sidebar.js";
import * as recording from "./recording.js";
import { configure, api } from "./api.js";
import { $, toast, closePopups } from "./dom.js";
import { mountPage, renderTopbar, closePeek } from "./page.js";
import { renderHome, renderSettings, renderAuth } from "./views.js";
import { quickSearch } from "./dialogs.js";
import { mountUpdateBar } from "./updates.js";
import { initDesktopNotifications } from "./inbox.js";

let settings = null;
let info = null;
let view = null; // página montada no momento
let saving = 0;

// ---------- Navegação ----------

async function render(route, { same } = {}) {
  closePopups();
  if (same && route.view === "page" && view?.id === route.id) return;
  closePeek(true);
  const prev = view;
  view = null;
  if (prev) {
    prev.flush().catch(() => {});
    prev.destroy();
  }
  const host = $("view");
  host.scrollTop = 0;
  if (route.view === "page") {
    view = mountPage(host, route.id);
    store.visited(route.id);
    sidebar.reveal(route.id);
    const created = store.cached(route.id);
    if (created && created.page.created_at > Date.now() - 3000 && created.page.kind !== "row") {
      requestAnimationFrame(() => view?.focusTitle());
    }
  } else if (route.view === "inbox") {
    (await import("./inbox.js")).renderInbox(host);
  } else if (route.view === "tasks") {
    (await import("./tasks.js")).renderTasks(host);
  } else if (route.view === "ai") {
    (await import("./ai-ui.js")).renderAsk(host, { initial: route.q, workspace: route.workspace, voice: route.voice });
  } else if (route.view === "team") {
    (await import("./team.js")).renderTeam(host, route);
  } else if (route.view === "admin") {
    (await import("./admin.js")).renderAdmin(host);
  } else if (route.view === "settings") {
    renderSettings(host, {
      settings, info,
      onSettings: (s) => { settings = s; recording.setSettings(s); },
      onToken: async (token) => { settings = await window.ordo.saveSettings({ token }); configure(settings); recording.setSettings(settings); },
      onSignOut: signOut,
    });
  } else {
    renderHome(host);
  }
  topbar();
  sidebar.schedule();
}

function topbar() {
  renderTopbar($("topbar"), router.current(), saving, view);
}

// ---------- Conta ----------

async function signIn() {
  configure(settings);
  // Quem entrou antes da 0.6.0 tem um token antigo ("ordo_…"): troca por uma
  // sessão do Better Auth (sem pedir a senha), senão o painel de sessões e as
  // rotas novas de conta respondem 401.
  if (settings.token?.startsWith("ordo_")) {
    try {
      const { token } = await api("POST", "/auth/upgrade", {});
      if (token) {
        settings = await window.ordo.saveSettings({ token });
        configure(settings);
        recording.setSettings(settings);
      }
    } catch { /* sem conexão ou servidor antigo: segue com o token antigo */ }
  }
  let me;
  try {
    me = await api("GET", "/me");
  } catch (err) {
    if (err.fatal) return showAuth("Sua sessão expirou. Entre de novo.");
    // Sem conexão: abre com o que estiver em cache.
    me = store.state.me || JSON.parse(localStorage.getItem("ordo.me") || "null");
    if (!me) return showAuth("Sem conexão com o servidor: " + err.message);
    toast("Sem conexão. Mostrando o que está salvo neste computador.", 6000);
  }
  try { localStorage.setItem("ordo.me", JSON.stringify(me)); } catch { /* ignore */ }
  // O app de Windows só oferece transcrever chamadas detectadas para administradores.
  const canTranscribe = me.role === "admin";
  if (!!settings.canTranscribe !== canTranscribe) {
    settings = await window.ordo.saveSettings({ canTranscribe });
    recording.setSettings(settings);
  }
  document.body.classList.remove("signed-out");
  $("auth").hidden = true;
  $("app").hidden = false;
  sidebar.mount($("sidebar"));
  const boot = store.init(me).catch((err) => toast("Não foi possível carregar as páginas: " + err.message));
  if (!store.state.ready) await boot;
  const last = router.lastRoute();
  router.go(last.view === "page" && !store.node(last.id) && !store.cached(last.id) ? { view: "home" } : last, { replace: true });
  // Aberto por um link ordo://page/… (antes de entrar ou com o app fechado).
  const link = waitingLink || await window.ordo.pendingLink?.().catch(() => null);
  waitingLink = null;
  if (link) openLink(link);
  // Reuniões antigas (de antes das páginas) viram páginas em "Reuniões".
  api("POST", "/meetings/adopt", {}).then((r) => { if (r.adopted) store.pollSoon(0); }).catch(() => {});
}

function showAuth(message) {
  document.body.classList.add("signed-out");
  $("app").hidden = true;
  $("auth").hidden = false;
  renderAuth($("auth"), {
    settings,
    message,
    onToken: async (token) => {
      settings = await window.ordo.saveSettings({ token });
      await signIn();
    },
  });
}

async function signOut() {
  if (recording.current) return toast("Encerre a transcrição antes de sair.");
  await view?.flush().catch(() => {});
  view?.destroy();
  view = null;
  // Encerra a sessão no servidor também (some da lista de sessões do admin).
  if (settings.token && !settings.token.startsWith("ordo_")) {
    await import("./auth-client.js").then(({ authClient }) => authClient().signOut()).catch(() => {});
  }
  settings = await window.ordo.saveSettings({ token: "" });
  store.signOut();
  try { localStorage.removeItem("ordo.me"); } catch { /* ignore */ }
  showAuth();
}

store.on("signed-out", () => showAuth("Sua sessão expirou. Entre de novo."));
store.on("saving", (n) => { saving = n; topbar(); });
store.on("presence", (pageId) => { if (view?.id === pageId) topbar(); });
store.on("tree", () => { if (router.current().view !== "settings") topbar(); });
store.on("offline", (off) => document.body.classList.toggle("offline", off));
store.on("notifications", () => sidebar.schedule());
initDesktopNotifications();
store.on("removed", (id) => {
  const r = router.current();
  if (r.view === "page" && r.id === id && view) {
    router.forget(id);
    router.go({ view: "home" }, { replace: true });
  }
});
router.setRenderer(render);

// ---------- Teclado e mouse ----------

document.addEventListener("keydown", (e) => {
  if (document.body.classList.contains("signed-out")) return;
  const mod = e.ctrlKey || e.metaKey;
  if (mod && e.key.toLowerCase() === "k") { e.preventDefault(); quickSearch(); }
  else if (mod && e.key.toLowerCase() === "n" && !e.shiftKey) { e.preventDefault(); sidebar.newPage(); }
  else if (mod && e.key === "\\") { e.preventDefault(); sidebar.toggleCollapsed(); }
  else if (e.altKey && e.key === "ArrowLeft") { e.preventDefault(); router.back(); }
  else if (e.altKey && e.key === "ArrowRight") { e.preventDefault(); router.forward(); }
  else if (mod && e.key === "[") { e.preventDefault(); router.back(); }
  else if (mod && e.key === "]") { e.preventDefault(); router.forward(); }
});
// Botões laterais do mouse (voltar/avançar).
window.addEventListener("mouseup", (e) => {
  if (e.button === 3) { e.preventDefault(); router.back(); }
  if (e.button === 4) { e.preventDefault(); router.forward(); }
});
// Barra lateral recolhida: aparece ao encostar o mouse na borda esquerda.
document.addEventListener("mousemove", (e) => {
  const app = $("app");
  if (!app.classList.contains("side-collapsed")) return;
  if (e.clientX < 8) app.classList.add("side-peek");
  else if (e.clientX > ($("sidebar").offsetWidth || 260) + 40) app.classList.remove("side-peek");
});
$("scrim").addEventListener("click", () => $("app").classList.remove("side-open"));
mountUpdateBar();
window.addEventListener("beforeunload", () => { view?.flush(); });

// ---------- Gravação: comandos do processo principal ----------

window.ordo.onCommand(async ({ command, app, auto, url }) => {
  switch (command) {
    case "start": {
      // Chamada detectada (o usuário aceitou no pop-up, ou início automático):
      // cria a página em "Reuniões" sem abrir a janela.
      if (!settings.token) {
        window.ordo.showWindow();
        return toast("Entre na sua conta para transcrever.");
      }
      if (!store.canTranscribe()) return;
      await recording.start({ app, auto, newPage: true });
      break;
    }
    case "stop": recording.stop(); break;
    case "pause": recording.pause(); break;
    case "resume": recording.resume(); break;
    case "show-notes":
    case "show-current": {
      const pageId = recording.current?.pageId || recording.lastPageId?.();
      if (pageId) router.openPage(pageId);
      break;
    }
    case "open-link": openLink(url); break;
    case "call-ended":
      if (recording.current?.auto && (!recording.current.app || recording.current.app === app)) {
        toast(`A chamada no ${app} terminou. Gerando o resumo…`);
        recording.stop();
      }
      break;
  }
});

// Links ordo://page/<id>, do "Copiar link" do Compartilhar.
let waitingLink = null;
function openLink(url) {
  const id = /^ordo:\/\/page\/([0-9a-f-]{36})/i.exec(url || "")?.[1];
  if (!id) return;
  if (!store.state.ready) { waitingLink = url; return; }
  router.openPage(id);
}

// ---------- Início ----------

(async function init() {
  settings = await window.ordo.getSettings();
  info = await window.ordo.appInfo();
  recording.setSettings(settings);
  configure(settings);
  if (!settings.token) showAuth();
  else await signIn();
})();
